/*
 * Copyright (c) Harness Inc.
 *
 * This source code is licensed under the Apache 2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { useCallback } from 'react'
import cx from 'classnames'
import { omit } from 'lodash-es'
import { Menu, MenuItem, Popover, PopoverInteractionKind, Position } from '@blueprintjs/core'

import { Text } from '../Text/Text'

import css from './SelectWithSubmenuV2.css'
import selectCss from '../Select/Select.css'
import { SelectProps, SelectOption, Select } from '../../components/Select/Select'
import { MultiTypeInputType } from '../../components/MultiTypeInput/MultiTypeInputUtils'

export interface SubmenuSelectOption extends SelectOption {
  submenuItems: SelectOption[]
  hasSubItems?: boolean
}

export interface SelectWithSubmenuPropsV2 extends Omit<SelectProps, 'items' | 'onChange'> {
  items: SubmenuSelectOption[]
  loading?: boolean
  onOpening?: (item: SelectOption) => void
  interactionKind?: PopoverInteractionKind
  onChange?: (primaryValue: SelectOption, selectedValue?: SelectOption, type?: MultiTypeInputType) => void
}

interface SubmenuProps {
  items: SelectOption[]
  loading?: boolean
  onChange?: (primaryValue: SelectOption, selectedValue: SelectOption, type?: MultiTypeInputType) => void
  primaryValue?: SelectOption
}
const SubmenuV2 = ({ items, onChange, primaryValue, loading }: SubmenuProps) => {
  return (
    <Menu className={css.submenu}>
      {loading ? (
        <div className={css.noResultsFound}>Loading...</div>
      ) : items?.length ? (
        items.map((item: any) => (
          <MenuItem
            key={item.value}
            text={item.label}
            onClick={() => {
              onChange?.(primaryValue as SelectOption, item)
            }}
            className={css.submenuItem}
          />
        ))
      ) : (
        <div className={css.noResultsFound}>No Results Found</div>
      )}
    </Menu>
  )
}

export function SelectWithSubmenuV2(props: SelectWithSubmenuPropsV2) {
  const { items, className, loading, onChange, ...selectProps } = props

  const itemRenderer = useCallback(
    (item: SelectOption) => {
      return (item as SubmenuSelectOption).hasSubItems ? (
        <Popover
          content={
            <SubmenuV2
              items={(item as SubmenuSelectOption).submenuItems}
              onChange={onChange}
              primaryValue={omit(item, 'submenuItems')}
              loading={loading}
            />
          }
          position={Position.LEFT_TOP}
          interactionKind={props?.interactionKind || PopoverInteractionKind.HOVER}
          onOpening={() => {
            if (props.onOpening) props.onOpening(item)
          }}
          minimal
          usePortal
          className={css.wrapperClassName}
          targetClassName={css.targetClassName}
          modifiers={{
            preventOverflow: {
              // This is required to always attach the popover portal to the start of the reference instead of the middle
              escapeWithReference: true
            },
            offset: {
              // This is required to offset the portal after it is attached to the reference.
              // By default the portal is positioned at top: 0, left:0 wrt it's reference
              // -2 is to adjust for the removed
              offset: '0 2'
            }
          }}>
          <li
            key={item.value?.toString()}
            className={cx(selectCss.menuItem, css.menuItem)}
            onClick={event => event.preventDefault()}>
            <Text rightIcon={'chevron-right'}>{item.label}</Text>
          </li>
        </Popover>
      ) : (
        <li
          key={item.value?.toString()}
          className={cx(selectCss.menuItem, css.menuItem)}
          onClick={() => {
            if (onChange) {
              onChange(item)
            }
          }}>
          <Text>{item.label}</Text>
        </li>
      )
    },
    [items, loading]
  )

  return (
    <Select
      {...selectProps}
      items={items}
      itemRenderer={itemRenderer}
      className={className}
      onChange={onChange as SelectProps['onChange']}
    />
  )
}
